import { Color, Font, Icon, Table, TableCol, TablePagination, TableSort, Text } from 'zens-storybook'
import React, { useState } from 'react'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { Trans, useTranslation } from 'react-i18next'
import {
  ProductsFilter,
  Product,
  useStore,
  titleForProductConnectivityState,
  productConnectivityStates,
  productPeripheralStates,
  titleForProductPeripheralState,
  useCompany,
  useBuildings,
  useFloors,
  useProducts,
  useFetchProducts,
} from 'zens-store'
import { ProductConnectivity, ProductPeripherals } from 'zens-fragment'
import { Href } from 'zens-router'
import * as XLSX from 'xlsx'

import { TablePaginated } from '../table'
import { MenuMore } from '../menu'

import ProductWorkspace from './workspace'
import ProductLocation from './location'
import ProductEmpty from './empty'
import ProductFilter from './filter'

const styles = StyleSheet.create({
  detail: {
    color: Color.metalGrey,
    fontSize: 14,
  },
  status: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  top: {
    flexDirection: 'row',
    justifyContent: 'center',
    zIndex: 10,
  },
  filter: {
    flex: 1,
  },
  moreMenu: {
    zIndex: 20,
  },
  more: {
    backgroundColor: Color.simplyWhite,
    borderBottomColor: Color.calmGrey,
    borderBottomWidth: 1,
  },
  moreTitle: {
    fontFamily: Font.medium,
  },
  download: {
    marginHorizontal: 10,
    height: 38,
  },
  skeletonMore: {
    width: 40,
    alignItems: 'center',
    height: 38,
    justifyContent: 'center',
  },
  help: {
    flex: 1,
  },
  helpText: {
    color: Color.simplyWhite,
  },
  helpTitle: {
    width: 170,
  },
})

interface Data {
  data: Product
  href: Href
}

interface Props {
  companyId: number
  pagination?: TablePagination
  onPaginationChange?: (pagination: TablePagination) => void
  sort?: TableSort
  onSortChange?: (sort: TableSort) => void
  filter?: ProductsFilter
  onFilterChange?: (filter: ProductsFilter) => void
}

const ProductTable = (props: Props) => {
  const {
    companyId,
    filter,
    pagination = { page: 0, size: 10 },
    onPaginationChange,
    sort,
    onSortChange,
    onFilterChange,
  } = props
  const [downloading, setDownloading] = useState(false)
  const { message } = useStore()
  const company = useCompany({ companyId })
  const buildings = useBuildings({ companyId })
  const floors = useFloors({ companyId })
  const fetchProducts = useFetchProducts()

  const products = useProducts({ companyId, pagination, sort, filter }, { enabled: !!filter })
  const { t } = useTranslation()
  const hasFilter = Object.keys(filter || {}).length > 0
  async function download() {
    try {
      setDownloading(true)
      const allProducts = await fetchProducts({
        companyId,
        sort: sort || { field: 'serial_number', direction: 'asc' },
        filter,
      })
      const floorData = filter?.floorId
        ?.map((floorId) => floors.data?.find((floor) => floor?.id === floorId))
        .map((floor) => {
          const building = buildings.data?.find((building) => building?.id === floor?.buildingId)
          return { floor, building }
        })
        .sort(
          (a, b) =>
            a.building?.name?.localeCompare(b.building?.name || '') ||
            (a.floor?.number || 0) -
              //test,
              (b.floor?.number || 0),
        )
      const connectivityStateData = filter?.connectivityState?.sort(
        (a, b) => productConnectivityStates.indexOf(a) - productConnectivityStates.indexOf(b),
      )
      const peripheralsStateData = filter?.peripheralsState?.sort(
        (a, b) => productPeripheralStates.indexOf(a) - productPeripheralStates.indexOf(b),
      )
      const rows: (string | number | Date)[][] = []
      rows.push([t('Company'), company.data?.name || '-'])
      if (floorData?.length)
        rows.push([
          t('Floor', { count: floorData.length }),
          floorData?.map(({ floor, building }) => `${building?.name}: ${floor?.displayName}`).join(', '),
        ])
      if (filter?.isInstalled !== undefined)
        rows.push([t('Installed'), filter?.isInstalled ? t('Installed') : t('Not installed')])
      if (connectivityStateData?.length)
        rows.push([
          t('Connectivity'),
          connectivityStateData?.map((state) => titleForProductConnectivityState(state, t)).join(', '),
        ])
      if (peripheralsStateData?.length)
        rows.push([
          t('Peripherals'),
          peripheralsStateData?.map((state) => titleForProductPeripheralState(state, t)).join(', '),
        ])
      rows.push([])
      rows.push([t('Serial No.'), t('Model'), t('Workspace'), t('Location'), t('Connectivity'), t('Peripherals')])
      allProducts.data?.forEach((product) => {
        const floor = floors.data?.find((item) => item?.id === product.floorId)
        const building = buildings.data?.find((item) => item?.id === floor?.buildingId)
        rows.push([
          product.serialNumber || '-',
          product.typeName || '-',
          product.workspaceName || '-',
          product.floorId ? `${floor?.displayName} (${building?.name})` : '-',
          titleForProductConnectivityState(product.connectivityState, t),
          titleForProductPeripheralState(product.peripheralsState, t),
        ])
      })

      const worksheet = XLSX.utils.aoa_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data')
      XLSX.writeFile(workbook, `${t('Products')}.xlsx`, { compression: true })
      setDownloading(false)
    } catch (error) {
      message.add({ message: t('Error generating data'), status: 'error' })
      console.error('Error generating data', error)
      setDownloading(false)
    }
  }
  const renderDetail = (product: Product) => {
    const { serialNumber, typeName } = product

    return (
      <View>
        <Text>{serialNumber || ' '}</Text>
        <Text style={styles.detail}>{typeName}</Text>
      </View>
    )
  }

  const renderWorkspace = (product: Product) => {
    if (!product.workspaceId) return <View />
    return <ProductWorkspace companyId={companyId} product={product} />
  }

  const renderLocation = (product: Product) => {
    if (!product.workspaceId) return <View />
    return <ProductLocation companyId={companyId} product={product} />
  }

  const renderConnectivityState = (product: Product) => {
    return (
      <View style={styles.status}>
        <ProductConnectivity value={product.connectivityState} />
      </View>
    )
  }

  const renderPeripheralsState = (product: Product) => {
    const { peripheralsState } = product
    return (
      <View style={styles.status}>
        <ProductPeripherals value={peripheralsState} />
      </View>
    )
  }

  const cols: TableCol<Product>[] = [
    {
      key: 'serial_number',
      title: t('Serial No./Model'),
      render: renderDetail,
      help: t('The serial number of the device'),
    },
    {
      key: 'workspace',
      title: t('Workspace'),
      render: renderWorkspace,
      help: t('The workspace where the product is installed'),
    },
    {
      key: 'floor',
      title: t('Location'),
      render: renderLocation,
      help: t('The location of the device'),
    },
    {
      key: 'connectivity_state',
      title: t('Connectivity'),
      render: renderConnectivityState,
      help: (
        <View style={styles.help}>
          <Text style={styles.helpText}>
            <Trans>Workspace connectivity status definitions</Trans>:
          </Text>
          <Text style={styles.helpText}>
            <View style={styles.helpTitle}>
              <Trans>Online</Trans>
            </View>
            <Trans>The device is connected and functioning correctly.</Trans>
          </Text>
          <Text style={styles.helpText}>
            <View style={styles.helpTitle}>
              <Trans>Connection lost</Trans>
            </View>
            <Trans>The device has been disconnected for at least 2 hours.</Trans>
          </Text>
          <Text style={styles.helpText}>
            <View style={styles.helpTitle}>
              <Trans>Offline</Trans>
            </View>
            <Trans>The device has been non-functional for 24 hours or more.</Trans>
          </Text>
          <Text style={styles.helpText}>
            <View style={styles.helpTitle}>
              <Trans>Never online</Trans>
            </View>
            <Trans>The device has never established a connection.</Trans>
          </Text>
        </View>
      ),
      size: {
        width: 150,
      },
    },
    {
      key: 'peripherals_state',
      title: t('Peripherals'),
      render: renderPeripheralsState,
      help: t(
        'Indicates whether all workspace peripherals are connected and functioning as expected. If a device is not online, this status will be reported as unknown.',
      ),
      size: {
        width: 150,
      },
    },
  ]

  const rows: Data[] =
    products.data?.data.map((product) => ({
      data: product,
      href: `/companies/${companyId}/products/${product.id}`,
    })) || []
  return (
    <>
      <View style={styles.top}>
        <ProductFilter
          companyId={companyId}
          filter={filter}
          onFilterChange={onFilterChange}
          loading={products.isFetching}
          style={styles.filter}
        />
        {downloading ? (
          <ActivityIndicator
            size={20}
            style={styles.download}
            color={Color.premiumBlack}
            testID="ProductTable.Spinner"
          />
        ) : (
          <MenuMore
            items={[
              {
                key: 'title',
                title: t('Download options'),
                disabled: true,
                style: styles.more,
                textStyle: styles.moreTitle,
              },

              {
                key: 'data',
                icon: 'download',
                title: t('Download data'),
                testID: 'ProductTable.Download',
              },
            ]}
            onSelect={download}
            testID="ProductTable.More"
            style={styles.moreMenu}
          />
        )}
      </View>
      <TablePaginated
        rows={rows}
        cols={cols}
        pagination={pagination}
        count={products.data?.meta?.total || 0}
        renderCount={() =>
          t('{{page}} of {{numberOfPages}}', {
            page: pagination.page + 1,
            numberOfPages: Math.ceil((products.data?.meta?.total || 0) / pagination.size),
          })
        }
        sizeLabel={t('Products per page')}
        onPaginationChange={onPaginationChange}
        sort={sort}
        onSortChange={onSortChange}
        dataIsSorted
        ListEmptyComponent={<ProductEmpty companyId={companyId} search={filter?.search} hasFilter={hasFilter} />}
      />
    </>
  )
}

const Skeleton = () => {
  const { t } = useTranslation()
  const cols: TableCol<Product>[] = [
    {
      key: 'serial_number',
      title: t('Serial No./Model'),
    },
    {
      key: 'workspace',
      title: t('Workspace'),
    },
    {
      key: 'floor',
      title: t('Location'),
    },
    {
      key: 'connectivity_state',
      title: t('Connectivity'),
      size: {
        width: 150,
      },
    },
    {
      key: 'peripherals_state',
      title: t('Peripherals'),
      size: {
        width: 150,
      },
    },
  ]
  return (
    <>
      <View style={styles.top}>
        <ProductFilter.Skeleton style={styles.filter} />
        <View style={styles.skeletonMore}>
          <Icon name="dots" color={Color.calmGrey} />
        </View>
      </View>
      <Table.Skeleton cols={cols} />
    </>
  )
}
ProductTable.Skeleton = Skeleton

export default ProductTable
